import { useState } from "react";
import { toast } from "sonner";
import { Search, Check, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

export function SuggestionList({ lead, emails, onSaved }) {
  const save = async (email) => {
    try { await api.patch(`/leads/${lead.id}`, { email }); await api.post(`/leads/${lead.id}/dismiss-suggestions`); toast.success(`Saved ${email} for ${lead.company_name}`); onSaved?.(email); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!emails?.length) return <span className="text-xs text-slate-400">No email published on the website</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {emails.map((e) => (
        <button key={e.email} onClick={() => save(e.email)} title={`Found on ${e.source}`} data-testid={`use-email-${lead.id}-${e.email}`}
          className={`inline-flex items-center gap-1 text-xs border rounded-full px-2.5 py-1 hover:bg-emerald-50 hover:border-emerald-400 transition-colors ${e.same_domain ? "border-blue-300 text-blue-800" : "text-slate-600"}`}>
          <Check size={11} />{e.email}
        </button>
      ))}
    </div>
  );
}

export function PeopleList({ lead, people, onSaved }) {
  const apply = async (p) => {
    try { await api.post(`/leads/${lead.id}/apply-person`, p); toast.success(`Contact set to ${p.first_name} ${p.last_name}`); onSaved?.(p); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!people?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {people.map((p, i) => (
        <button key={i} onClick={() => apply(p)} data-testid={`use-person-${lead.id}-${i}`}
          className="inline-flex items-center gap-1 text-xs border border-violet-300 text-violet-900 rounded-full px-2.5 py-1 hover:bg-emerald-50 hover:border-emerald-400 transition-colors">
          <UserCheck size={11} /><b>{p.first_name} {p.last_name}</b>{p.job_title && <span className="text-slate-500">· {p.job_title}</span>}{p.email && !lead.email && <span className="text-blue-700">· {p.email}</span>}
        </button>
      ))}
    </div>
  );
}

export function FindEmailButton({ lead, onSaved }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/leads/${lead.id}/find-emails`); setRes(data); if (data.error) toast.error(data.error); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (lead.email && lead.first_name) return null;
  return (
    <div className="space-y-2" data-testid="find-email-block">
      <Button size="sm" variant="outline" disabled={busy || !lead.website} onClick={go} data-testid="find-email-btn" title={lead.website ? "" : "Add a website first"}>
        <Search size={14} className="mr-1" />{busy ? "Searching website…" : lead.email ? "Find contact name" : "Find email & contact"}
      </Button>
      {res && !lead.email && <SuggestionList lead={lead} emails={res.emails} onSaved={onSaved} />}
      {res && !lead.first_name && (res.people?.length ? <PeopleList lead={lead} people={res.people} onSaved={onSaved} /> : <div className="text-xs text-slate-400">No named people found on the website</div>)}
    </div>
  );
}
